import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { DocumentThemeSlug, ErrorAlert, JobAccepted } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface UseStyleApplyResult {
  apply: (versionId: string, themeSlug: DocumentThemeSlug) => Promise<JobAccepted>;
  isApplying: boolean;
  error: ErrorAlert | null;
  jobId: string | null;
  reset: () => void;
}

/**
 * F65 "Apply & re-render" (E19 mutation states: idle/loading/success/error).
 * `style.apply` is the COMMIT — a live client-side preview
 * (`documentThemes.ts`'s visual approximation) precedes it with no server
 * round trip; this only fires once the reader clicks Apply. No moderation
 * gate (pure presentation, F65). Success returns a `jobId`, tracked via the
 * existing `JobProgressCard`/`useJobSnapshot` (`features/jobs`) rather than
 * a bespoke progress UI.
 */
export function useStyleApply(documentId: string): UseStyleApplyResult {
  const { wsClient } = useWs();
  const [jobId, setJobId] = useState<string | null>(null);

  const mutation = useMutation<JobAccepted, ErrorAlert, { versionId: string; themeSlug: DocumentThemeSlug }>({
    mutationFn: ({ versionId, themeSlug }) => requestTyped(wsClient, 'style.apply', { documentId, versionId, themeSlug }),
    onSuccess: (result) => setJobId(result.jobId),
  });

  const apply = useCallback(
    (versionId: string, themeSlug: DocumentThemeSlug) => mutation.mutateAsync({ versionId, themeSlug }),
    [mutation],
  );

  const reset = useCallback(() => {
    setJobId(null);
    mutation.reset();
  }, [mutation]);

  return {
    apply,
    isApplying: mutation.isPending,
    error: mutation.isError ? mutation.error : null,
    jobId,
    reset,
  };
}
